/**
 * Intelligems Visitor Sync (With Historical Merging)
 * Persists and merges experiment data across multiple page views via localStorage
 * Only sends API call when the cumulative merged data has changed
 */
(function () {
  const CONFIG = {
    apiEndpoint: 'https://ngpbeeqn65.execute-api.us-east-2.amazonaws.com/prod/ig/sync',
    localStorageKeys: {
      visitorId: 'mc_visitor_id',
      historicalExperiments: 'ig_historical_experiments', // Stores the persistent, merged list
      lastSyncedData: 'ig_last_synced_data',              // Stores exactly what the API successfully received last
    },
    igRetryDelays: [10, 100, 1000, 5000, 10000, 50000],
    apiRetryDelays: [10000, 20000, 30000, 40000],
  };

  /**
   * Wait for Intelligems to be available with retry logic
   */
  async function waitForIntelligems(retryIndex = 0) {
    if (window.igData && window.igData.user) {
      return window.igData;
    }

    if (retryIndex >= CONFIG.igRetryDelays.length) {
      console.warn('[IG Sync] Intelligems not available after all retries');
      return null;
    }

    const delay = CONFIG.igRetryDelays[retryIndex];
    await new Promise((resolve) => setTimeout(resolve, delay));
    return waitForIntelligems(retryIndex + 1);
  }

  function getVisitorId() {
    try {
      return localStorage.getItem(CONFIG.localStorageKeys.visitorId) || null;
    } catch (e) {
      console.error('[IG Sync] Error reading visitor ID:', e);
      return null;
    }
  }

  /**
   * Reads historically captured experiments from localStorage
   */
  function getHistoricalExperiments() {
    try {
      const stored = localStorage.getItem(CONFIG.localStorageKeys.historicalExperiments);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('[IG Sync] Error reading historical experiments:', e);
      return [];
    }
  }

  /**
   * Get Intelligems experiment data in scope on the CURRENT page
   */
  function getCurrentPageExperiments() {
    try {
      const experiments = window.igData?.user?.getExperiments?.() || [];
      return experiments.map((experiment) => {
        const variant = window.igData?.user?.getTestGroup?.(experiment.id);
        return {
          id: experiment.id,
          name: experiment.name,
          variant: variant?.name ?? 'unknown',
        };
      });
    } catch (e) {
      console.error('[IG Sync] Error getting current experiments:', e);
      return [];
    }
  }

  /**
   * Merges historical and current experiments using ID as a unique key
   */
  function mergeExperiments(history, current) {
    // Use a Map keyed by experiment ID to enforce uniqueness and update records
    const registry = new Map();

    // 1. Load history first
    history.forEach(exp => {
      if (exp && exp.id) registry.set(exp.id, exp);
    });

    // 2. Overwrite or add current page experiments
    current.forEach(exp => {
      if (exp && exp.id) registry.set(exp.id, exp);
    });

    // 3. Return as a clean array
    return Array.from(registry.values());
  }

  function buildSyncData(visitorId, mergedExperiments) {
    return {
      visitor_id: visitorId,
      // Sort ensures deterministic JSON string comparison
      experiments: [...mergedExperiments].sort((a, b) => String(a.id).localeCompare(String(b.id))),
    };
  }

  /**
   * Compares payload against what the backend API successfully saw last
   */
  function hasDataChanged(currentPayload) {
    try {
      const lastSyncedRaw = localStorage.getItem(CONFIG.localStorageKeys.lastSyncedData);
      if (!lastSyncedRaw) return true;

      return lastSyncedRaw !== JSON.stringify(currentPayload);
    } catch (e) {
      console.error('[IG Sync] Error checking data change:', e);
      return true;
    }
  }

  function saveLocalState(historicalExperiments, syncedPayload) {
    try {
      localStorage.setItem(CONFIG.localStorageKeys.historicalExperiments, JSON.stringify(historicalExperiments));
      if (syncedPayload) {
        localStorage.setItem(CONFIG.localStorageKeys.lastSyncedData, JSON.stringify(syncedPayload));
      }
    } catch (e) {
      console.error('[IG Sync] Error saving local state:', e);
    }
  }

  async function sendToApi(data, retryIndex = 0) {
    try {
      const response = await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      console.log('[IG Sync] Data synced successfully');
      return true;
    } catch (e) {
      console.error(`[IG Sync] API error (attempt ${retryIndex + 1}):`, e);
      if (retryIndex >= CONFIG.apiRetryDelays.length) return false;

      const delay = CONFIG.apiRetryDelays[retryIndex];
      await new Promise((resolve) => setTimeout(resolve, delay));
      return sendToApi(data, retryIndex + 1);
    }
  }

  /**
   * Main Orchestrator
   */
  async function sync() {
    const visitorId = getVisitorId();
    if (!visitorId) return;

    const igData = await waitForIntelligems();
    if (!igData) return;

    // 1. Gather data from past sessions/pages and the current page
    const historicalExps = getHistoricalExperiments();
    const currentPageExps = getCurrentPageExperiments();

    // 2. Compute the cumulative, merged set of experiments
    const mergedExps = mergeExperiments(historicalExps, currentPageExps);

    // 3. Always save the merged registry locally right away
    // This ensures local storage stays complete even if network drops
    saveLocalState(mergedExps, null);

    // 4. Construct payload and check if backend is missing anything
    const syncData = buildSyncData(visitorId, mergedExps);
    if (!hasDataChanged(syncData)) {
      console.log('[IG Sync] Overall experiment profile unchanged, skipping API sync');
      return;
    }

    // 5. Fire off payload. Update synchronization record only on HTTP success
    console.log('[IG Sync] Change detected. Transmitting historical + current payload...', syncData);
    const success = await sendToApi(syncData);
    if (success) {
      saveLocalState(mergedExps, syncData);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();


// /**
//  * Intelligems Visitor Sync
//  * Maps visitor ID to Intelligems experiment data and syncs to backend
//  * Only sends API call when data has changed (compared via sorted JSON)
//  */
// (function () {
//   const CONFIG = {
//     apiEndpoint: 'https://ngpbeeqn65.execute-api.us-east-2.amazonaws.com/prod/ig/sync',
//     localStorageKeys: {
//       visitorId: 'mc_visitor_id',
//       lastSync: 'ig_last_sync',
//     },
//     igRetryDelays: [10, 100, 1000, 5000, 10000, 50000],
//     apiRetryDelays: [10000, 20000, 30000, 40000],
//   };

//   /**
//    * Wait for Intelligems to be available with retry logic
//    * @returns {Promise<object|null>} igData or null if not available after retries
//    */
//   async function waitForIntelligems(retryIndex = 0) {
//     if (window.igData && window.igData.user) {
//       return window.igData;
//     }

//     if (retryIndex >= CONFIG.igRetryDelays.length) {
//       console.warn('[IG Sync] Intelligems not available after all retries');
//       return null;
//     }

//     const delay = CONFIG.igRetryDelays[retryIndex];
//     console.log(`[IG Sync] Waiting ${delay / 1000}s for Intelligems (attempt ${retryIndex + 1}/${CONFIG.igRetryDelays.length})`);

//     await new Promise((resolve) => setTimeout(resolve, delay));
//     return waitForIntelligems(retryIndex + 1);
//   }

//   /**
//    * Get visitor ID from localStorage
//    * @returns {string|null}
//    */
//   function getVisitorId() {
//     try {
//       return localStorage.getItem(CONFIG.localStorageKeys.visitorId) || null;
//     } catch (e) {
//       console.error('[IG Sync] Error reading visitor ID:', e);
//       return null;
//     }
//   }

//   /**
//    * Get Intelligems experiment data
//    * @returns {Array<{id: string, name: string, variant: string}>}
//    */
//   function getIgExperiments() {
//     try {
//       const experiments = window.igData?.user?.getExperiments?.() || [];
//       return experiments.map((experiment) => {
//         const variant = window.igData?.user?.getTestGroup?.(experiment.id);
//         return {
//           id: experiment.id,
//           name: experiment.name,
//           variant: variant?.name ?? 'unknown',
//         };
//       });
//     } catch (e) {
//       console.error('[IG Sync] Error getting experiments:', e);
//       return [];
//     }
//   }

//   /**
//    * Build sorted sync data object for comparison
//    * @param {string} visitorId
//    * @param {Array} experiments
//    * @returns {object}
//    */
//   function buildSyncData(visitorId, experiments) {
//     return {
//       visitor_id: visitorId,
//       experiments: [...experiments].sort((a, b) => String(a.id).localeCompare(String(b.id))),
//     };
//   }

//   /**
//    * Check if data has changed since last sync
//    * @param {object} currentData
//    * @returns {boolean}
//    */
//   function hasDataChanged(currentData) {
//     try {
//       const lastSync = localStorage.getItem(CONFIG.localStorageKeys.lastSync);
//       if (!lastSync) return true;

//       const currentJson = JSON.stringify(currentData);
//       return lastSync !== currentJson;
//     } catch (e) {
//       console.error('[IG Sync] Error checking data change:', e);
//       return true; // Sync if we can't determine
//     }
//   }

//   /**
//    * Save sync data to localStorage
//    * @param {object} data
//    */
//   function saveSyncData(data) {
//     try {
//       localStorage.setItem(CONFIG.localStorageKeys.lastSync, JSON.stringify(data));
//     } catch (e) {
//       console.error('[IG Sync] Error saving sync data:', e);
//     }
//   }

//   /**
//    * Send data to API with retry logic
//    * @param {object} data
//    * @param {number} retryIndex
//    * @returns {Promise<boolean>} success
//    */
//   async function sendToApi(data, retryIndex = 0) {
//     try {
//       const response = await fetch(CONFIG.apiEndpoint, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(data),
//       });

//       if (!response.ok) {
//         throw new Error(`HTTP ${response.status}`);
//       }

//       console.log('[IG Sync] Data synced successfully');
//       return true;
//     } catch (e) {
//       console.error(`[IG Sync] API error (attempt ${retryIndex + 1}):`, e);

//       if (retryIndex >= CONFIG.apiRetryDelays.length) {
//         console.error('[IG Sync] API failed after all retries');
//         return false;
//       }

//       const delay = CONFIG.apiRetryDelays[retryIndex];
//       console.log(`[IG Sync] Retrying API in ${delay / 1000}s`);

//       await new Promise((resolve) => setTimeout(resolve, delay));
//       return sendToApi(data, retryIndex + 1);
//     }
//   }

//   /**
//    * Main sync function
//    */
//   async function sync() {
//     // Get visitor ID first
//     const visitorId = getVisitorId();
//     if (!visitorId) {
//       console.log('[IG Sync] No visitor ID found, skipping sync');
//       return;
//     }

//     // Wait for Intelligems
//     const igData = await waitForIntelligems();
//     if (!igData) {
//       return;
//     }

//     // Get experiment data (empty array is valid - means experiments ended)
//     const experiments = getIgExperiments();

//     // Build sorted data for comparison
//     const syncData = buildSyncData(visitorId, experiments);

//     // Check if data changed
//     if (!hasDataChanged(syncData)) {
//       console.log('[IG Sync] Data unchanged, skipping sync');
//       return;
//     }

//     console.log('[IG Sync] Data changed, syncing...', syncData);

//     // Send to API
//     const success = await sendToApi(syncData);

//     // Only save if API succeeded (so it retries on next page load if failed)
//     if (success) {
//       saveSyncData(syncData);
//     }
//   }

//   // Initialize on DOM ready
//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', sync);
//   } else {
//     sync();
//   }
// })();
